import ROUTES from 'config/routes'
import React, { useContext, useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Offcanvas from 'react-bootstrap/Offcanvas'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import TableRowStatus from 'routes/Transits/Table/TableRowStatus'
import { UserContext } from 'shared/users/contexts/UserContext'
import PdfPreviewModal from 'components/PdfPreviewModal'
import LoadingSpinner from 'components/LoadingSpinner'
import { TransitOperation } from 'features/declaration/transit-operation/models/types'
import { TransitOperationInfoResponse } from 'api/models'
import { hasText } from 'shared/lib/common-util'
import TransitPreviewActions from './TransitPreviewActions'
import { useFetchTransitOperationInfo } from '../transit-operation-info/api/fetchTransitOperationInfo'

export type PreviewView = 'data' | 'logs'

export interface TransitPreviewHeaderProps {
  transitOperationId: number
  transitStatus: string
  transitOperation: TransitOperation | undefined
  currentView: PreviewView
  changeView: (view: PreviewView) => void
  originalRow: any
  openAuditModal?: () => void
  openSigningModal?: () => void
}

function TransitPreviewHeader(props: Readonly<TransitPreviewHeaderProps>) {
  const {
    transitOperationId,
    changeView,
    currentView,
    transitStatus,
    transitOperation,
    originalRow,
    openAuditModal,
    openSigningModal,
  } = props

  const { t } = useTranslation()
  const history = useNavigate()
  const { user } = useContext(UserContext)
  const { pathname } = useLocation()
  const [transitOperationInfo, setTransitOperationInfo] = useState<TransitOperationInfoResponse>()
  const [isPdfPreviewModalVisible, setIsPdfPreviewModalVisible] = useState<boolean>(false)
  const isAuditView = pathname.includes(ROUTES.transitAudit)

  const getTransitOperationInfo = useFetchTransitOperationInfo(transitOperationId, user?.role === 'ADMIN')

  const isEns = transitOperation?.declarationType === 'ENS'

  useEffect(() => {
    setTransitOperationInfo(getTransitOperationInfo.data)
  }, [transitOperationId, getTransitOperationInfo.data])

  const toggleView = () => {
    if (currentView === 'logs') {
      changeView('data')
    } else {
      changeView('logs')
    }
  }
  const openTransit = () => {
    if (isEns) {
      history(`${ROUTES.ens}/${transitOperationId}`)
      return
    }

    history(`${ROUTES.transitP5}/${transitOperationId}`)
  }

  return (
    <Offcanvas.Header
      className="border-bottom"
      closeButton
    >
      <div
        className="d-flex flex-grow-1 justify-content-between "
      >
        <div className="d-flex align-items-end ">

          <h1 className="text-dark me-2">{transitOperationId}</h1>
          <TableRowStatus value={transitStatus} />
          <div className="d-flex ms-2 ps-1 flex-column">
            {
              hasText(transitOperation?.mrn) && (
                <small>{transitOperation?.mrn}</small>
              )
            }
            {
              hasText(transitOperation?.lrn) && (
                <small>{transitOperation?.lrn}</small>
              )
            }
          </div>

        </div>
        <div className="d-flex align-items-center">
          {(user?.role === 'ADMIN' && isAuditView && ['IN_REVIEW', 'PROHIBITED_DESCRIPTION'].includes(originalRow.status)) && (
            <div className="me-1">
              {openSigningModal && (
                <Button
                  className="me-1"
                  variant="light"
                  size="lg"
                  onClick={openSigningModal}
                  disabled={getTransitOperationInfo.isLoading}
                >
                  {getTransitOperationInfo.isLoading && <LoadingSpinner />}
                  {!getTransitOperationInfo.isLoading && (
                    <>
                      <i className="fal fa-key me-2" />
                      <span>{t('buttons.signAndSubmit')}</span>
                    </>
                  )}
                </Button>
              )}
              <Button
                className="me-1"
                variant="light"
                size="lg"
                onClick={() => {
                  const auditModalFn = openAuditModal ?? (() => null)
                  auditModalFn()
                }}
                disabled={getTransitOperationInfo.isLoading}
              >
                {
                  getTransitOperationInfo.isLoading && <LoadingSpinner />
                }
                {
                  !getTransitOperationInfo.isLoading && <span>{t('buttons.decideAudit')}</span>
                }
              </Button>
            </div>
          )}
          <Button
            className="rounded-circle wh-34 border-secondary d-flex justify-content-center align-items-end me-2"
            variant="light"
            size="lg"
            onClick={() => setIsPdfPreviewModalVisible(true)}
          >
            <i className="fal fa-file-pdf" />
          </Button>
          <PdfPreviewModal
            isPdfPreviewModalVisible={isPdfPreviewModalVisible}
            setPdfPreviewModalVisible={setIsPdfPreviewModalVisible}
            declarationId={transitOperationId}
            isPhase5
          />
          <Button
            className="me-2"
            variant="outline-dark"
            size="lg"
            onClick={toggleView}
          >
            <i className={`${currentView === 'logs' ? 'fa-times' : 'fa-align-right'} fal me-2`} />
            <span>
              {currentView === 'logs' ? t('buttons.viewLogs', { context: 'close' }) : t('buttons.viewLogs')}
            </span>
          </Button>
          <Button
            className="me-3 text-primary"
            variant="dark"
            size="lg"
            onClick={openTransit}
          >
            <i className="fal me-2 fa-pen" />
            <span>
              {t('common.edit')}
            </span>
          </Button>
          <div className="me-3">
            <TransitPreviewActions
              currentView={currentView}
              transitOperationInfo={transitOperationInfo}
              transitOperation={transitOperation}
              originalRow={originalRow}
              transitOperationId={transitOperationId}
              isAuditView={isAuditView}
            />
          </div>
        </div>
      </div>
    </Offcanvas.Header>
  )
}

export default TransitPreviewHeader
